import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { CadObject } from '../../../../models/cad-object.model';
import { CadViewerComponent } from '../../../cad-viewer/cad-viewer.component';
import { CadPropertiesComponent } from '../../../shared/cad-properties/cad-properties.component';
import { CadFileService } from '../../../services/cad-file.service';
import { MouldingGraphService } from '../../services/moulding-graph.service';
import { MouldingProcessingService } from '../../services/moulding-processing.service';
import { CadInputPickerDialogComponent } from '../cad-input-picker-dialog/cad-input-picker-dialog.component';
import { CadObjectTreeComponent } from '../../../shared/cad-object-tree/cad-object-tree.component';

type CadDetails = {
  cadFileId: number;
  filename: string;
  cadObject: CadObject;
};

@Component({
  selector: 'app-cad-input-node-config-dialog',
  standalone: true,
  imports: [
    ButtonModule,
    DialogModule,
    TooltipModule,
    CadViewerComponent,
    CadPropertiesComponent,
    CadInputPickerDialogComponent,
    CadObjectTreeComponent,
  ],
  templateUrl: './cad-input-node-config-dialog.component.html',
})
export class CadInputNodeConfigDialogComponent implements OnChanges {
  private readonly graph = inject(MouldingGraphService);
  readonly processing = inject(MouldingProcessingService);
  private readonly cadFileService = inject(CadFileService);

  @Input({ required: true }) nodeId!: string;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  readonly details = signal<CadDetails | null>(null);
  readonly loading = signal(false);
  readonly pickerVisible = signal(false);
  hoveredCadObjectId: number | null = null;
  visibleCadObjectIds: Set<number> | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      void this.loadDetails();
    }
  }

  onVisibleChange(visible: boolean): void {
    this.visible = visible;
    this.visibleChange.emit(visible);
    if (!visible) {
      this.hoveredCadObjectId = null;
      this.reset();
    }
  }

  openPicker(): void {
    if (this.processing.downstreamExecutionActive()) {
      return;
    }
    this.pickerVisible.set(true);
  }

  get canChangeCad(): boolean {
    return !this.processing.downstreamExecutionActive();
  }

  onPickerVisibleChange(visible: boolean): void {
    this.pickerVisible.set(visible);
  }

  async onConfigured(): Promise<void> {
    this.pickerVisible.set(false);
    this.hoveredCadObjectId = null;
    await this.loadDetails();
  }

  reset(): void {
    this.details.set(null);
    this.loading.set(false);
    this.pickerVisible.set(false);
  }

  private async loadDetails(): Promise<void> {
    const artifact = this.graph.getNodeData(this.nodeId, 'cad_input')?.result
      ?.artifact;
    if (!artifact) {
      this.details.set(null);
      return;
    }

    this.details.set(null);
    this.loading.set(true);
    try {
      const [cadFile, loadedCadObjects] = await Promise.all([
        firstValueFrom(this.cadFileService.getCadFile(artifact.cad_file_id)),
        firstValueFrom(
          this.cadFileService.loadCadObjects([artifact.primary_cad_object_id]),
        ),
      ]);
      const cadObject = loadedCadObjects[0];
      if (!cadObject) return;
      this.details.set({
        cadFileId: cadFile.id,
        filename: cadFile.filename,
        cadObject,
      });
    } catch {
      this.details.set(null);
    } finally {
      this.loading.set(false);
    }
  }
}
